import { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { usersApi, DbUser } from '../api/client';

interface CurrentUser {
  session: Session | null;
  dbUser: DbUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

let cachedDbUser: DbUser | null = null;
let fetchPromise: Promise<DbUser> | null = null;

export function useCurrentUser(): CurrentUser {
  const [session, setSession] = useState<Session | null>(null);
  const [dbUser, setDbUser] = useState<DbUser | null>(cachedDbUser);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      // Only clear when there's ALSO no app-JWT session — password login stores a
      // 'token' in localStorage without a Supabase session, and Supabase fires an
      // initial null-session event on mount that would otherwise wipe that user.
      if (!session && !localStorage.getItem('token')) {
        cachedDbUser = null;
        fetchPromise = null;
        setDbUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Resolve the DB user from a Supabase session OR an app-JWT ('token' in
    // localStorage from email/password login). Either proves identity to /users/me.
    const hasAppToken = !!localStorage.getItem('token');
    if (!session && !hasAppToken) {
      setDbUser(null);
      return;
    }
    if (cachedDbUser) {
      setDbUser(cachedDbUser);
      return;
    }
    if (!fetchPromise) {
      const email = session?.user?.email || '';
      fetchPromise = usersApi.me().then((r) => r.data).catch(() =>
        email ? usersApi.ensure(email).then((r) => r.data) : Promise.reject(new Error('no user')),
      );
    }
    fetchPromise.then((u) => {
      cachedDbUser = u;
      setDbUser(u);
    }).catch(() => {});
  }, [session]);

  return {
    session,
    dbUser,
    isLoading,
    isAuthenticated: !!session,
  };
}
